import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { readThemePref, writeThemePref, resolveTheme, applyResolvedTheme, } from '../lib/theme';
const ThemeContext = createContext(null);
/**
 * Owns the live theme: reads the stored pref, resolves 'system' via the host's
 * SystemSchemeDetector, applies `data-theme`, and re-applies when the host
 * scheme changes (only observable while pref === 'system'). Idempotent with the
 * pre-paint `applyThemeAtBoot` call in the host entry (that one prevents the
 * first-paint flash; this one keeps it in sync + exposes the selector state).
 */
export function ThemeProvider({ detector, children, }) {
    const [pref, setPrefState] = useState(() => readThemePref());
    const [systemScheme, setSystemScheme] = useState(() => detector.getScheme());
    // Track host scheme changes (matters only while pref === 'system').
    useEffect(() => detector.subscribe(() => setSystemScheme(detector.getScheme())), [detector]);
    const resolved = resolveTheme(pref, systemScheme);
    // Apply on every resolved change.
    useEffect(() => applyResolvedTheme(resolved), [resolved]);
    const setPref = useCallback((next) => {
        writeThemePref(next);
        setPrefState(next);
    }, []);
    const value = useMemo(() => ({ pref, resolved, setPref }), [pref, resolved, setPref]);
    return _jsx(ThemeContext.Provider, { value: value, children: children });
}
export function useTheme() {
    const ctx = useContext(ThemeContext);
    if (!ctx)
        throw new Error('useTheme must be used within ThemeProvider');
    return ctx;
}
//# sourceMappingURL=ThemeProvider.js.map